import { Config, Effect, Result, Schema, SchemaIssue, Predicate } from 'effect';
import { defineConfigKey } from '@listening/env/config-key';

export const permanentStages = [
  'production',
  'staging',
  'development',
] as const;
export type PermanentStage = (typeof permanentStages)[number];

export const isPermanentStage = (stage: Stage): stage is PermanentStage =>
  permanentStages.includes(stage as PermanentStage);

/** Permanent stages + pr-* preview stages */
export const isValidStage = (stage: string) =>
  isPermanentStage(stage) || stage.startsWith('pr-');

/**
 * Physical S3 bucket holding `POST /api/v2/ocr` debug captures (submitted page
 * image + raw recognizer output, 3-day lifecycle), or `null` where no bucket
 * exists and capture must stay off.
 *
 * ONE source of truth for both sides: `packages/infra/src/stage.ts`
 * names the bucket with this, and the runtime capture layer resolves its target
 * with this — a hardcoded name on either side would drift silently and write
 * captures nowhere (or worse, somewhere real).
 *
 * - `development` / `staging` — own a bucket each; captures stay separated by stage.
 * - `pr-*` — no bucket of their own (preview stages get no k8s at all and run
 *   against the development backend), so they resolve to development's.
 * - `production` — null. These objects are user document images; production
 *   never captures, and there is no bucket for it to write to.
 * - `localstack` — null. Local dev has no such bucket provisioned.
 */
export const ocrDebugCaptureBucketName = (stage: Stage): string | null => {
  if (stage === 'development' || stage.startsWith('pr-'))
    return 'listening-ocr-debug-capture-development';
  if (stage === 'staging') return 'listening-ocr-debug-capture-staging';
  return null;
};

export const validModes = [
  'localstack',
  'development',
  'staging',
  'production',
] as const;

export type Mode = (typeof validModes)[number];
export type Stage = Mode | (string & {});

const isValidMode = (stage: Stage): stage is Mode =>
  validModes.includes(stage as Mode);

export const fallbackMode: Mode = 'localstack';

export function modeFromStage(stage: Stage): Mode {
  if (isValidMode(stage)) return stage;
  return 'development';
}

export function getStageFromEnv(): Stage | null {
  const stage = typeof process !== 'undefined' ? process.env.STAGE : undefined;
  return stage || null;
}

/**
 * Pipeable fallback: when process.env lookup fails, try import.meta.env (Vite client builds).
 * @see https://vite.dev/guide/env-and-mode
 *
 * When import.meta.env also misses, this re-reads the same key rather than
 * raising a hand-rolled failure: `Config.withDefault`/`Config.option` rescue
 * only *absence*, and absence is carried by the config's own unresolved
 * reading, not by any error value. Synthesizing a failure here would look like
 * invalid data and silently defeat every downstream `.withDefault(...)`/
 * `.option` on a `viteString(...)` config (it would break `modeConfig` and
 * `publicDocTutorialExperimentFlagKeyConfig`, which must fall back when neither
 * source has the var — e.g. loading apps/start's vite.config.ts under plain
 * Node). A key that *is* present but undecodable still fails loudly.
 *
 * `Config.orElse`'s callback argument is untyped-`undefined` at runtime, so it
 * is deliberately unused.
 */
export const orViteEnv = (name: string) =>
  Config.orElse(() => {
    try {
      const value = (
        import.meta as ImportMeta & { env?: Record<string, unknown> }
      ).env?.[name];
      if (Predicate.isString(value) && value !== '')
        return Config.succeed(value);
    } catch {
      // import.meta.env unavailable (non-Vite context)
    }
    return Config.String(name);
  });

/**
 * Like Config.string but falls back to import.meta.env for Vite client bundles.
 * process.env (server) is tried first, then import.meta.env as fallback.
 */
export const viteString = (name: string): Config.Config<string> =>
  Config.String(name).pipe(orViteEnv(name));

// process.env.MODE (server) first, then import.meta.env.VITE_MODE (Vite client bundles).
// import.meta.env.MODE is Vite's build mode ('production'), NOT the app mode — use VITE_MODE instead.
// Vitest sets import.meta.env.MODE='test', so we accept 'test' and map it to 'localstack'.
export const modeConfig = defineConfigKey('MODE', (name) =>
  Config.String(name).pipe(
    Config.orElse(() => viteString('VITE_MODE')),
    Config.map((m) => (m === 'test' ? 'localstack' : m)),
    Config.mapEffect((m) =>
      isValidMode(m)
        ? Effect.fromResult(Result.succeed(m))
        : Effect.fromResult(
            Result.fail(
              new Config.ConfigError(
                new Schema.SchemaError(
                  new SchemaIssue.InvalidValue(
                    {
                      message: `Expected one of (${validModes.join(', ')}) but received "${m}"`,
                    },
                    m,
                  ),
                ),
              ),
            ),
          ),
    ),
    Config.withDefault(modeFromStage(getStageFromEnv() ?? fallbackMode)),
  ),
);
